import { useRef } from 'react';
import AtmosphericFog from '../../Components/AtmosphericFog';
import BkButton from '../../Components/ui/BkButton';
import KingPlaceholder from '../../Components/KingPlaceholder';
import { usePlayModalContext } from '../../contexts/PlayModalContext';
import useBoardOrientation from '../../hooks/use-board-orientation';
import usePageMeta from '../../hooks/use-page-meta';
import { BoardCanvas } from '../Game/pixi';
import useHome from './hooks/use-home';

export default function Home() {
  usePageMeta({
    title: 'Blind Kings',
    description:
      'Blind Kings — a hidden-piece strategy game. Reveal, strike, and outwit your rival online or locally.',
    path: '/',
  });

  const { showPlayModal, openPlayModal } = usePlayModalContext();
  const { board } = useHome(showPlayModal);
  const previewRef = useRef<HTMLDivElement>(null);
  const { renderVertically } = useBoardOrientation(previewRef);

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden">
      <div
        ref={previewRef}
        className="absolute inset-0 z-0 overflow-hidden bg-bk-bg contain-strict"
      >
        <div className="h-full w-full scale-[1.06] origin-center">
          <BoardCanvas
            board={board}
            interactive={false}
            displayPieceValues={false}
            renderVertically={renderVertically}
            preview
            paused={showPlayModal}
          />
        </div>
      </div>

      <div className="bk-hero-grade pointer-events-none absolute inset-0 z-[1]" aria-hidden />
      <AtmosphericFog />

      <article className="sr-only">
        <h1>Blind Kings</h1>
        <p>
          Blind Kings is a two-player hidden-information strategy game. Armies
          start face-down on a 4×8 board. Reveal, move, and attack to conquer
          your rival kingdom. Play locally, against the computer, or online.
        </p>
        <ol>
          <li>
            Reveal any face-down piece. The first reveal is always the first
            player’s piece; later flips can belong to either side.
          </li>
          <li>
            Move one of your revealed pieces into an empty adjacent square.
          </li>
          <li>
            Attack an adjacent enemy with equal or lower Combat Power. King,
            Peasant, and Assassin have specials.
          </li>
          <li>
            Capture the enemy King, or reduce your rival to no living pieces, to
            win.
          </li>
        </ol>
      </article>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-end px-4 pb-16 pt-10 sm:px-8 sm:pb-20 lg:px-12 lg:pb-24">
        <div className="bk-landing-rise flex w-full max-w-2xl flex-col items-center gap-4 text-center sm:gap-5">
          <h1 className="text-5xl font-extrabold leading-none tracking-tight text-bk-text sm:text-7xl md:text-8xl">
            Blind<span className="text-bk-gold-bright">Kings</span>
          </h1>
          <div
            className="h-px w-28 bg-gradient-to-r from-transparent via-bk-gold to-transparent sm:w-36"
            aria-hidden
          />
          <p className="max-w-md text-base leading-relaxed text-bk-muted sm:text-lg">
            Reveal, strike, and outwit your rival in a game of hidden power.
          </p>
          <div className="pt-2">
            <BkButton
              variant="primary"
              size="lg"
              className="min-w-[11rem] px-10 py-3.5 text-lg transition-colors duration-150 sm:text-xl"
              onClick={openPlayModal}
            >
              Play Now
            </BkButton>
          </div>
        </div>
      </div>
    </div>
  );
}
